import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL, resendVerificationEmail } from '../../utils/accountApi';
import './SignupForm.css';
import signupImage from '../../assets/images/Automation5.webp';

const initialData = {
  fullName: '',
  email: '',
  password: '',
  repeatPassword: '',
  companyName: '',
  phoneNumber: '',
  shippingAddress: '',
  zipCode: '',
  country: '',
  billingAddress: '',
  website: '',
  agreeToTerms: false
};

function SignUpForm() {
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [countries, setCountries] = useState([]);
  const [countryError, setCountryError] = useState('');
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);
  const [submitError, setSubmitError] = useState('');
  const [confirmationEmailSent, setConfirmationEmailSent] = useState(null);
  const [resending, setResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendError, setResendError] = useState('');
  const submitLockRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();

    async function loadCountries() {
      setIsLoadingCountries(true);
      try {
        const response = await fetch(`${API_BASE_URL}/api/countries`, {
          signal: controller.signal
        });
        if (!response.ok) throw new Error(`Country request failed: ${response.status}`);
        setCountries(await response.json());
        setCountryError('');
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Unable to load countries:', error);
          setCountryError('Countries could not be loaded. Refresh the page to try again.');
        }
      } finally {
        if (!controller.signal.aborted) setIsLoadingCountries(false);
      }
    }

    loadCountries();
    return () => controller.abort();
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value
    }));
    setErrors((current) => ({ ...current, [name]: '' }));
    setSubmitError('');
  };

  const validate = () => {
    const next = {};
    if (formData.fullName.trim().length < 2) next.fullName = 'Enter your full name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) next.email = 'Enter a valid email address.';
    if (formData.password.length < 12) next.password = 'Use at least 12 characters.';
    if (formData.password !== formData.repeatPassword) next.repeatPassword = 'Passwords do not match.';
    if (!formData.companyName.trim()) next.companyName = 'Enter your company name.';
    if (!/^\d{4,10}$/.test(formData.zipCode)) next.zipCode = 'Use 4 to 10 digits.';
    if (!/^\+?[0-9\s\-()]{7,20}$/.test(formData.phoneNumber)) next.phoneNumber = 'Enter a valid phone number.';
    if (!formData.shippingAddress.trim()) next.shippingAddress = 'Enter the shipping address.';
    if (!formData.billingAddress.trim()) next.billingAddress = 'Enter the billing address.';
    if (!formData.country) next.country = 'Select a country.';
    if (!formData.agreeToTerms) next.agreeToTerms = 'Please accept the terms and conditions.';
    return next;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitLockRef.current) return;

    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      document.querySelector('.su-field-error')?.closest('.su-field')?.querySelector('input, select')?.focus();
      return;
    }

    submitLockRef.current = true;
    setSubmitting(true);
    setSubmitError('');
    try {
      const registerResponse = await fetch(`${API_BASE_URL}/api/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      const registration = await registerResponse.json();
      if (!registerResponse.ok) throw new Error(registration.error || 'Account registration failed.');
      setConfirmationEmailSent(registration.confirmationEmailSent !== false);
      setSubmitted(true);
    } catch (error) {
      setSubmitError(error.message || 'We could not create your account. Please try again.');
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    setResendMessage('');
    setResendError('');
    try {
      await resendVerificationEmail(formData.email);
      setConfirmationEmailSent(true);
      setResendMessage('A new verification email was sent. Please check your inbox and spam folder.');
    } catch (error) {
      setResendError(error.message || 'The verification email could not be sent.');
    } finally {
      setResending(false);
    }
  };

  if (submitted) {
    return (
      <main className="su-page">
        <section className="su-confirmation">
          <span className="su-success-icon"><i className="fas fa-envelope-open-text" /></span>
          <span className="su-eyebrow">One last step</span>
          <h1>{confirmationEmailSent ? 'Check your inbox.' : 'Your account was created.'}</h1>
          <p>
            {confirmationEmailSent
              ? <>We sent a confirmation link to <strong>{formData.email}</strong>. Verify your email to activate your Valley account.</>
              : <>The first email could not be sent to <strong>{formData.email}</strong>. Use the button below to try again.</>}
          </p>
          {resendMessage && <div className="su-resend-status su-resend-status--success" role="status">{resendMessage}</div>}
          {resendError && <div className="su-resend-status su-resend-status--error" role="alert">{resendError}</div>}
          <button className="su-secondary-button" type="button" onClick={handleResend} disabled={resending}>
            {resending ? 'Sending…' : 'Resend verification email'}
          </button>
          <Link to="/" className="su-primary-button">Return to home <span>→</span></Link>
        </section>
      </main>
    );
  }

  return (
    <main className="su-page">
      <div className="su-shell">
        <aside className="su-aside">
          <img src={signupImage} alt="" aria-hidden="true" />
          <div className="su-aside-overlay" />
          <div className="su-aside-copy">
            <span className="su-eyebrow">Your Valley account</span>
            <h1>One place for every order.</h1>
            <p>Create an account to move from product selection to payment and order tracking without losing context.</p>
          </div>
          <div className="su-benefits">
            <div><i className="fas fa-shopping-cart" /><span><strong>Keep your cart</strong><small>Carry selected products into checkout.</small></span></div>
            <div><i className="fas fa-file-invoice" /><span><strong>Manage orders</strong><small>Review details and complete pending payments.</small></span></div>
            <div><i className="fas fa-headset" /><span><strong>Get support</strong><small>Keep your company and delivery details together.</small></span></div>
          </div>
        </aside>

        <section className="su-form-panel">
          <div className="su-form-heading">
            <span className="su-eyebrow su-eyebrow-dark">Create an account</span>
            <h2>Let’s get your profile ready.</h2>
            <p>Already registered? Use <strong>Log in</strong> in the header above.</p>
          </div>

          {submitError && (
            <div className="su-alert" role="alert">
              <i className="fas fa-exclamation-circle" />
              <span>{submitError}</span>
            </div>
          )}

          <form className="su-form" onSubmit={handleSubmit} noValidate>
            <div className="su-bot-field" aria-hidden="true">
              <label htmlFor="signup-website">Website</label>
              <input
                id="signup-website"
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                tabIndex="-1"
                autoComplete="off"
              />
            </div>
            <div className="su-section-label"><span>01</span><p>Account details</p></div>
            <div className="su-grid">
              <Field label="Full name" name="fullName" value={formData.fullName} onChange={handleChange} error={errors.fullName} autoComplete="name" required />
              <Field label="Work email" name="email" type="email" value={formData.email} onChange={handleChange} error={errors.email} autoComplete="email" required />
              <Field label="Password" name="password" type="password" value={formData.password} onChange={handleChange} error={errors.password} autoComplete="new-password" hint="At least 12 characters" required />
              <Field label="Repeat password" name="repeatPassword" type="password" value={formData.repeatPassword} onChange={handleChange} error={errors.repeatPassword} autoComplete="new-password" required />
            </div>

            <div className="su-section-label"><span>02</span><p>Company & contact</p></div>
            <div className="su-grid">
              <Field label="Company name" name="companyName" value={formData.companyName} onChange={handleChange} error={errors.companyName} autoComplete="organization" required />
              <Field label="Phone number" name="phoneNumber" type="tel" value={formData.phoneNumber} onChange={handleChange} error={errors.phoneNumber} autoComplete="tel" placeholder="+1 956 000 0000" required />
            </div>

            <div className="su-section-label"><span>03</span><p>Delivery information</p></div>
            <div className="su-grid">
              <Field className="su-field-wide" label="Shipping address" name="shippingAddress" value={formData.shippingAddress} onChange={handleChange} error={errors.shippingAddress} autoComplete="shipping street-address" required />
              <div className={`su-field ${errors.country ? 'has-error' : ''}`}>
                <label htmlFor="signup-country">Country *</label>
                <select id="signup-country" name="country" value={formData.country} onChange={handleChange} disabled={isLoadingCountries || Boolean(countryError)} required>
                  <option value="">{isLoadingCountries ? 'Loading countries…' : 'Select country'}</option>
                  {countries.map((country) => (
                    <option key={country.idPais} value={country.Country}>{country.Country}</option>
                  ))}
                </select>
                {errors.country && <span className="su-field-error">{errors.country}</span>}
                {countryError && <span className="su-field-error">{countryError}</span>}
              </div>
              <Field label="Zip code" name="zipCode" value={formData.zipCode} onChange={handleChange} error={errors.zipCode} autoComplete="shipping postal-code" required />
              <Field className="su-field-wide" label="Billing address" name="billingAddress" value={formData.billingAddress} onChange={handleChange} error={errors.billingAddress} autoComplete="billing street-address" required />
            </div>

            <label className={`su-terms ${errors.agreeToTerms ? 'has-error' : ''}`}>
              <input type="checkbox" name="agreeToTerms" checked={formData.agreeToTerms} onChange={handleChange} />
              <span className="su-terms-check"><i className="fas fa-check" /></span>
              <span>I agree to Valley International Supply’s <Link to="/terms" target="_blank" rel="noopener noreferrer">terms and conditions</Link> and acknowledge its <Link to="/privacy" target="_blank" rel="noopener noreferrer">privacy policy</Link>.</span>
            </label>
            {errors.agreeToTerms && <span className="su-field-error su-terms-error">{errors.agreeToTerms}</span>}

            <button className="su-submit" type="submit" disabled={submitting || Boolean(countryError)}>
              {submitting ? 'Creating your account…' : 'Create account'}
              <span>{submitting ? '·' : '→'}</span>
            </button>
            <p className="su-privacy"><i className="fas fa-lock" /> Your information is used only to manage your account, orders and service requests.</p>
          </form>
        </section>
      </div>
    </main>
  );
}

function Field({ label, name, type = 'text', value, onChange, error, hint, className = '', ...props }) {
  return (
    <div className={`su-field ${className} ${error ? 'has-error' : ''}`}>
      <label htmlFor={`signup-${name}`}>{label}{props.required ? ' *' : ''}</label>
      <input id={`signup-${name}`} type={type} name={name} value={value} onChange={onChange} {...props} />
      {error ? <span className="su-field-error">{error}</span> : hint ? <span className="su-field-hint">{hint}</span> : null}
    </div>
  );
}

export default SignUpForm;
